import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { fetchTelecineMxGap } from "../../service/telecineGapService";
import "./index.css";

const pageSizeOptions = [25, 50, 100, 250].map((value) => ({
  label: `${value}`,
  value,
}));

const sortFieldOptions = [
  { label: "Origem", value: "source" },
  { label: "International Title", value: "international_title" },
  { label: "Título original", value: "titulo_original" },
  { label: "IMDb Code", value: "imdb_code" },
  { label: "Ano", value: "production_year" },
  { label: "IMDb Rating", value: "imdb_rating" },
  { label: "IMDb Votes", value: "imdb_rating_count" },
];

const sortOrderOptions = [
  { label: "Ascendente", value: "asc" },
  { label: "Descendente", value: "desc" },
];

export default function TelecineMxGapPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [yearMin, setYearMin] = useState(null);
  const [yearMax, setYearMax] = useState(null);
  const [ratingMin, setRatingMin] = useState(null);
  const [sortField, setSortField] = useState("international_title");
  const [sortOrder, setSortOrder] = useState("asc");
  const [meta, setMeta] = useState(null);

  const queryParams = useMemo(
    () => ({
      page,
      page_size: pageSize,
      search: search || undefined,
      year_min: yearMin ?? undefined,
      year_max: yearMax ?? undefined,
      rating_min: ratingMin ?? undefined,
      sort_field: sortField,
      sort_order: sortOrder,
    }),
    [page, pageSize, search, yearMin, yearMax, ratingMin, sortField, sortOrder],
  );

  const loadData = async (customParams = queryParams) => {
    setLoading(true);
    try {
      const data = await fetchTelecineMxGap(customParams);
      setRows(data.results || []);
      setTotalRecords(data.meta?.total || 0);
      setMeta(data.meta || null);
    } catch (error) {
      console.error("Erro ao carregar Telecine MX Gap:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [queryParams]);

  const onSearchSubmit = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const onClearFilters = () => {
    setPage(1);
    setPageSize(50);
    setSearchInput("");
    setSearch("");
    setYearMin(null);
    setYearMax(null);
    setRatingMin(null);
    setSortField("international_title");
    setSortOrder("asc");
  };

  const exportToXlsx = async () => {
    try {
      setLoading(true);
      const data = await fetchTelecineMxGap({
        ...queryParams,
        export: true,
        page: 1,
        page_size: 50000,
      });

      const exportRows = (data.results || []).map((item) => ({
        Source: item.source,
        "International Title": item.international_title,
        "Original Title": item.titulo_original,
        "IMDb Code": item.imdb_code,
        "IMDb Rating": item.imdb_rating,
        "IMDb Votes": item.imdb_rating_count,
        "Production Year": item.production_year,
        "JustWatch URL": item.justwatch_url,
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Telecine MX Gap");
      XLSX.writeFile(workbook, "telecine_br_not_svod_mx.xlsx");
    } catch (error) {
      console.error("Erro ao exportar XLSX:", error);
    } finally {
      setLoading(false);
    }
  };

  const ratingBodyTemplate = (row) => {
    if (row.imdb_rating === null || row.imdb_rating === undefined) {
      return <Tag severity="warning" value="Sem nota" />;
    }
    return <span>{Number(row.imdb_rating).toFixed(1)}</span>;
  };

  const votesBodyTemplate = (row) =>
    row.imdb_rating_count
      ? Number(row.imdb_rating_count).toLocaleString("pt-BR")
      : "-";

  const linkBodyTemplate = (row) => {
    if (!row.justwatch_url) return "-";
    return (
      <a href={row.justwatch_url} target="_blank" rel="noreferrer">
        Abrir oferta
      </a>
    );
  };

  const titleBodyTemplate = (row) => (
    <div>
      <div className="telecine-gap__title-main">
        {row.international_title || "-"}
      </div>
      {row.titulo_original &&
      row.titulo_original !== row.international_title ? (
        <small className="telecine-gap__title-sub">{row.titulo_original}</small>
      ) : null}
    </div>
  );

  const sourceBodyTemplate = (row) => {
    if (row.source === "vod_core") {
      return <Tag severity="warning" value="VOD Core" />;
    }
    return <Tag severity="success" value="Filmelier" />;
  };

  return (
    <div className="grid telecine-gap-page">
      <div className="col-12">
        <Card
          title="Telecine BR x SVOD MX"
          subTitle="União de ofertas vindas do Filmelier e da tabela espelho do VOD Core"
        >
          <div className="telecine-gap-toolbar">
            <span className="p-input-icon-left telecine-gap-search">
              <i className="pi pi-search" />
              <InputText
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearchSubmit()}
                placeholder="Buscar por título ou IMDb Code"
              />
            </span>

            <InputNumber
              value={yearMin}
              onValueChange={(e) => setYearMin(e.value)}
              placeholder="Ano mín."
              useGrouping={false}
            />

            <InputNumber
              value={yearMax}
              onValueChange={(e) => setYearMax(e.value)}
              placeholder="Ano máx."
              useGrouping={false}
            />

            <InputNumber
              value={ratingMin}
              onValueChange={(e) => setRatingMin(e.value)}
              placeholder="IMDb mín."
              minFractionDigits={1}
              maxFractionDigits={1}
            />

            <Dropdown
              value={sortField}
              options={sortFieldOptions}
              onChange={(e) => setSortField(e.value)}
              placeholder="Ordenar por"
            />

            <Dropdown
              value={sortOrder}
              options={sortOrderOptions}
              onChange={(e) => setSortOrder(e.value)}
              placeholder="Ordem"
            />

            <Dropdown
              value={pageSize}
              options={pageSizeOptions}
              onChange={(e) => {
                setPage(1);
                setPageSize(e.value);
              }}
              placeholder="Page size"
            />

            <Button
              label="Aplicar"
              icon="pi pi-filter"
              onClick={onSearchSubmit}
            />
            <Button
              label="Limpar"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={onClearFilters}
            />
            <Button
              label="Exportar XLSX"
              icon="pi pi-file-excel"
              className="p-button-success"
              onClick={exportToXlsx}
            />
          </div>

          <div className="telecine-gap-summary">
            <Tag
              severity="info"
              value={`Total união: ${totalRecords.toLocaleString("pt-BR")}`}
            />

            {meta?.source_stats?.filmelier_results_count !== undefined ? (
              <Tag
                severity="success"
                value={`Filmelier: ${Number(meta.source_stats.filmelier_results_count).toLocaleString("pt-BR")}`}
              />
            ) : null}

            {meta?.source_stats?.vod_core_results_count !== undefined ? (
              <Tag
                severity="warning"
                value={`VOD Core: ${Number(meta.source_stats.vod_core_results_count).toLocaleString("pt-BR")}`}
              />
            ) : null}

            {meta?.catalog_stats?.filmelier_movies_count !== undefined ? (
              <Tag
                severity="secondary"
                value={`Catálogo Filmelier: ${Number(meta.catalog_stats.filmelier_movies_count).toLocaleString("pt-BR")}`}
              />
            ) : null}

            {meta?.catalog_stats?.vod_core_titles_count !== undefined ? (
              <Tag
                severity="secondary"
                value={`Catálogo VOD Core: ${Number(meta.catalog_stats.vod_core_titles_count).toLocaleString("pt-BR")}`}
              />
            ) : null}

            {meta?.provider?.name ? (
              <Tag
                severity="success"
                value={`Provider: ${meta.provider.name}`}
              />
            ) : null}

            {meta?.rule ? <Tag severity="warning" value={meta.rule} /> : null}
          </div>

          <DataTable
            value={rows}
            loading={loading}
            paginator
            lazy
            rows={pageSize}
            totalRecords={totalRecords}
            first={(page - 1) * pageSize}
            onPage={(e) => {
              setPage(e.page + 1);
              setPageSize(e.rows);
            }}
            responsiveLayout="scroll"
            stripedRows
            showGridlines
            emptyMessage="Nenhum filme encontrado para os filtros selecionados."
          >
            <Column
              header="Origem"
              body={sourceBodyTemplate}
              style={{ width: "9rem" }}
            />
            <Column
              header="Título"
              body={titleBodyTemplate}
              style={{ minWidth: "22rem" }}
            />
            <Column
              field="imdb_code"
              header="IMDb Code"
              style={{ minWidth: "10rem" }}
            />
            <Column
              field="imdb_rating"
              header="IMDb Rating"
              body={ratingBodyTemplate}
              style={{ width: "9rem" }}
            />
            <Column
              field="imdb_rating_count"
              header="IMDb Votes"
              body={votesBodyTemplate}
              style={{ width: "10rem" }}
            />
            <Column
              field="production_year"
              header="Ano"
              style={{ width: "7rem" }}
            />
            <Column
              header="JustWatch"
              body={linkBodyTemplate}
              style={{ width: "10rem" }}
            />
          </DataTable>
        </Card>
      </div>
    </div>
  );
}
